// Totem / Balcão (venda rápida): orquestra os hooks (dados, carrinho, checkout,
// fluxo) e escolhe a tela. Substitui o antigo pages/BalcaoVendas.js de 2.229
// linhas — cada tela e cada regra vive no seu arquivo em features/totem/.
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import ChipCliente from "./ChipCliente";
import AvisoCaixaFechado, { TELA_COM_AVISO } from "./components/AvisoCaixaFechado";
import PopupBoasVindas from "./PopupBoasVindas";
import TelaIdentificacao from "./TelaIdentificacao";
import { useOfertasNoCarrinho } from "../ofertas-do-dia";
import { guardarPassagem, itensParaDelivery } from "../balcao-entrega";

import { useCaixaAberto } from "./hooks/useCaixaAberto";
import { useCarrinho } from "./hooks/useCarrinho";
import { apagarCarrinhoGuardado, useCarrinhoGuardado } from "./hooks/useCarrinhoGuardado";
import { useCheckout } from "./hooks/useCheckout";
import { useDescontoBalcao } from "./hooks/useDescontoBalcao";
import { useLeitorBalcao } from "./hooks/useLeitorBalcao";
import { useModoConsumo } from "./hooks/useModoConsumo";
import { useTotemDados } from "./hooks/useTotemDados";
import { useTotemFluxo } from "./hooks/useTotemFluxo";
import TelaCardapio from "./screens/TelaCardapio";
import TelaCardapioBalcao from "./screens/TelaCardapioBalcao";
import TelaConferencia from "./screens/TelaConferencia";
import TelaConfirmacao from "./screens/TelaConfirmacao";
import TelaInicial from "./screens/TelaInicial";
import TelaPagamento from "./screens/TelaPagamento";
import TelaProduto from "./screens/produto/TelaProduto";
import TelaTipoConsumo from "./screens/TelaTipoConsumo";

export default function TotemApp({ autoAtendimento = false, setAutoAtendimento }) {
  const navigate = useNavigate();
  const resetRef = useRef(() => {});
  const fluxo = useTotemFluxo({ autoAtendimento, onReset: () => resetRef.current() });
  const dados = useTotemDados({ autoAtendimento, autoAtendimentoRef: fluxo.autoAtendimentoRef });
  const carrinho = useCarrinho({ products: dados.products, allProductsForPhotos: dados.allProductsForPhotos, categories: dados.categories, promotions: dados.promotions });
  // Desconto (% / R$ / cupom) no rodapé do carrinho — só no Balcão (atendente).
  // No Totem o cliente não digita desconto, mas o prêmio do Cartão Fidelidade entra
  // pelo mesmo caminho do cupom (features/cartao-fidelidade/PremioNoBalcao).
  // `modulo` é o canal da venda: a reconferência do cupom precisa do certo (31/10/2026).
  const desconto = useDescontoBalcao({ ativo: true, subtotal: carrinho.cartTotal, itens: carrinho.cart, modulo: autoAtendimento ? "Totem" : "Balcao" });
  const checkout = useCheckout({
    autoAtendimento, cashbackCfg: dados.cashbackCfg, cart: carrinho.cart, cartTotal: carrinho.cartTotal,
    deliveryType: fluxo.deliveryType, allProductsForPhotos: dados.allProductsForPhotos,
    getItemSubitems: carrinho.getItemSubitems, getItemStepsSummary: carrinho.getItemStepsSummary,
    getProductPromo: carrinho.getProductPromo, setScreen: fluxo.setScreen, setOrderCode: fluxo.setOrderCode,
    desconto,
  });
  desconto.clienteRef.current = checkout.cliente;
  // Campanha Inteligente: o CPF chega depois dos itens — a oferta entra no carrinho quando o cliente é identificado.
  const ofertasCliente = useOfertasNoCarrinho({
    clienteId: checkout.cliente?.id || null, modulo: autoAtendimento ? "Totem" : "Balcao",
    cart: carrinho.cart, setCart: carrinho.setCart,
  });
  // Balcão: o carrinho (com cliente e desconto) sobrevive ao F5 — sessionStorage da aba.
  useCarrinhoGuardado({
    ativo: !autoAtendimento, screen: fluxo.screen, cart: carrinho.cart, setCart: carrinho.setCart,
    cliente: checkout.cliente, setCliente: checkout.setCliente, customerName: checkout.customerName, setCustomerName: checkout.setCustomerName,
    desconto: desconto.desconto, setDesconto: desconto.setDesconto,
  });
  resetRef.current = () => { carrinho.limpar(); checkout.limpar(); desconto.limparDesconto(); dados.loadData(); };
  // Balcão: Comer no Local/Para Viagem ou, na mercearia, Levar agora/Entrega.
  const { modo: modoConsumo, entregaDisponivel } = useModoConsumo(!autoAtendimento);
  // "Entrega" (mercearia): o carrinho vai para o Novo Pedido do Delivery (features/balcao-entrega).
  const passarParaEntrega = () => {
    const itens = itensParaDelivery(carrinho.cart, { getItemSubitems: carrinho.getItemSubitems, getItemStepsSummary: carrinho.getItemStepsSummary });
    // O desconto vai junto: o Novo Pedido do Delivery abre com ele aplicado.
    const descontoPassado = desconto.montante > 0 ? desconto.desconto : null;
    if (!guardarPassagem({ itens, cliente: checkout.cliente || null, nome: checkout.customerName || "", desconto: descontoPassado })) {
      toast.error("Não foi possível levar o pedido para o Delivery. Tente de novo.");
      return;
    }
    carrinho.limpar(); checkout.limpar(); fluxo.setScreen("menu"); apagarCarrinhoGuardado();
    navigate("/admin/delivery");
  };

  const chipCliente = (extra = "") => (
    <ChipCliente nome={checkout.cliente?.nome || checkout.customerName} foto={checkout.cliente?.foto} saldo={checkout.cashbackAtivo && checkout.cliente ? checkout.saldoCashback : null} className={extra} />
  );
  const popupBoasVindas = <PopupBoasVindas mensagem={checkout.boasVindas} onFim={() => checkout.setBoasVindas(null)} />;
  const { screen, setScreen } = fluxo;

  // Produto no TOTEM abre em tela inteira (screens/produto); no balcão fica o popup.
  // `origemProduto` guarda de onde veio (cardápio ou conferência) pra voltar pro mesmo lugar.
  const [origemProduto, setOrigemProduto] = useState("menu");
  const abrirProduto = (p) => {
    if (carrinho.pizza.tryOpenPizza(p)) return;
    carrinho.setSelectedProduct(p);
    if (autoAtendimento) { setOrigemProduto(screen === "cart_review" ? "cart_review" : "menu"); setScreen("produto"); }
    else carrinho.setPopupOpen(true);
  };
  const fecharProduto = () => { carrinho.setSelectedProduct(null); setScreen(origemProduto); };
  // Leitor USB: só no Balcão (atendente). O totem de autoatendimento não tem leitor.
  const leitor = useLeitorBalcao({
    ativo: !autoAtendimento, screen, products: dados.products, allProducts: dados.allProductsForPhotos, cart: carrinho.cart,
    addToCart: carrinho.addToCart, comPromocao: carrinho.comPromocao, abrirProduto, tryOpenPizza: carrinho.pizza.tryOpenPizza,
    novaVenda: fluxo.resetTotem,
  });
  const addSuggestionToCart = (p) => {
    if (carrinho.pizza.tryOpenPizza(p)) return;
    if (p.order_steps?.length > 0 || p.product_type === "combo") abrirProduto(p);
    else carrinho.addToCart({ ...carrinho.comPromocao(p), quantity: 1 });
  };
  // Balcão sem caixa aberto: faixa vermelha no alto e as telas descem para ela caber.
  const caixaAberto = useCaixaAberto({ ativo: !autoAtendimento, screen });
  const avisoCaixa = !autoAtendimento && caixaAberto === false;
  const ctx = { ...fluxo, ...dados, ...carrinho, ...checkout, desconto, ofertasCliente, autoAtendimento, setAutoAtendimento, navigate, chipCliente, popupBoasVindas, abrirProduto, fecharProduto, addSuggestionToCart, modoConsumo, entregaDisponivel, passarParaEntrega, lerCodigo: autoAtendimento ? null : leitor.lerCodigo };
  if (avisoCaixa) ctx.telaCls = TELA_COM_AVISO;
  // Sempre o mesmo fragmento: a faixa aparecer ou sumir não remonta a tela.
  return <>{avisoCaixa && <AvisoCaixaFechado />}{telaAtual()}</>;

  function telaAtual() {
    if (screen === "welcome") return <TelaInicial ctx={ctx} />;
    if (screen === "delivery_type") return <TelaTipoConsumo ctx={ctx} />;
    if (screen === "menu") return autoAtendimento ? <TelaCardapio ctx={ctx} /> : <TelaCardapioBalcao ctx={ctx} />;
    if (screen === "produto") return <TelaProduto ctx={ctx} />;
    if (screen === "cart_review") return <TelaConferencia ctx={ctx} />;
    if (screen === "payment") return <TelaPagamento ctx={ctx} />;
    if (screen === "confirmation") return <TelaConfirmacao ctx={ctx} />;
    if (screen === "identificacao") {
      const { etapaIdent, setEtapaIdent, pixPendente, setPixPendente, cashbackPendente, setCashbackPendente, cashbackAtivo, cashbackGanho, setCliente, setCustomerName, setBoasVindas, submitOrder } = checkout;
      return (
        <TelaIdentificacao
          etapa={etapaIdent}
          setEtapa={setEtapaIdent}
          motivoPix={pixPendente}
          cashback={cashbackAtivo ? { percentual: dados.cashbackCfg?.percentual, ganho: cashbackGanho } : null}
          motivoCashback={cashbackPendente}
          onIdentificado={(c) => {
            setCliente(c);
            setCustomerName(c.nome || "");
            const primeiro = (c.nome || "").trim().split(/\s+/)[0];
            setBoasVindas(c.novo ? `Prazer em te conhecer, ${primeiro}!` : `Seja bem-vindo de volta, ${primeiro}!`);
            if (pixPendente) { setPixPendente(false); setScreen("payment"); submitOrder(c); return; }
            if (cashbackPendente) { setCashbackPendente(false); setScreen("payment"); return; }
            setScreen("menu");
          }}
          onPular={() => { setCliente(null); setScreen("menu"); }}
          onVoltar={() => {
            if (pixPendente) { setPixPendente(false); setScreen("payment"); return; }
            if (cashbackPendente) { setCashbackPendente(false); setScreen("payment"); return; }
            setScreen(autoAtendimento ? "welcome" : "menu");
          }}
        />
      );
    }
    return null;
  }
}
